import { Checkbox, Money, Text } from '@belong/ui';
import { clsx } from 'clsx';
import type { ConsumerFailedPaymentModel } from '../failed-payments.types';
import {
  formatShortDate,
  getBalanceUniqueIds,
  isFutureDueDate,
} from '../failed-payments-utils';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';

type WithholdingSubsectionProps = {
  failedPayments: ConsumerFailedPaymentModel[];
  withholdingFullPayment: boolean | null;
  withheldBalanceUniqueIds: string[];
  onSelectFullPayment: (value: boolean) => void;
  onToggleBalanceIds: (balanceUniqueIds: string[]) => void;
};

export function WithholdingSubsection({
  failedPayments,
  withholdingFullPayment,
  withheldBalanceUniqueIds,
  onSelectFullPayment,
  onToggleBalanceIds,
}: WithholdingSubsectionProps) {
  const selectedFailedPayments = failedPayments.filter((failedPayment) =>
    getBalanceUniqueIds(failedPayment).some((balanceUniqueId) =>
      withheldBalanceUniqueIds.includes(balanceUniqueId),
    ),
  );
  const futureSelectedPayments = selectedFailedPayments.filter(
    (failedPayment) => isFutureDueDate(failedPayment.dueOn),
  );
  const hasFutureSelectedPayment = futureSelectedPayments.length > 0;

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-md">
        <Text variant="h3" fontWeight="semibold">
          {FAILED_PAYMENTS_STRINGS['withholding.full_payment_prompt']}
        </Text>
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2 md:grid-cols-3">
          {[true, false].map((value) => {
            const isSelected = withholdingFullPayment === value;
            const label = value
              ? FAILED_PAYMENTS_STRINGS['withholding.full_payment_yes']
              : FAILED_PAYMENTS_STRINGS['withholding.full_payment_no'];

            return (
              <button
                key={String(value)}
                type="button"
                onClick={() => onSelectFullPayment(value)}
                className={clsx(
                  'flex h-[64px] w-full cursor-pointer items-center justify-center rounded border-0 px-md text-center shadow transition-colors',
                  isSelected ? 'bg-navy' : 'bg-white',
                )}
              >
                <Text
                  variant="body"
                  fontWeight={isSelected ? 'semibold' : 'regular'}
                  className={isSelected ? 'text-white' : 'text-navy'}
                >
                  {label}
                </Text>
              </button>
            );
          })}
        </div>
      </div>

      {withholdingFullPayment === false && (
        <div className="flex flex-col gap-sm">
          <Text variant="h3" fontWeight="semibold">
            {FAILED_PAYMENTS_STRINGS['withholding.balances_prompt']}
          </Text>

          <div className="flex flex-col gap-md">
            {failedPayments.map((failedPayment) => {
              const balanceIds = getBalanceUniqueIds(failedPayment);
              const balanceUniqueId = balanceIds[0];
              if (!balanceUniqueId) return null;

              const isSelected = balanceIds.some((id) =>
                withheldBalanceUniqueIds.includes(id),
              );

              return (
                <div
                  key={balanceUniqueId}
                  role="button"
                  tabIndex={0}
                  onClick={() => onToggleBalanceIds(balanceIds)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onToggleBalanceIds(balanceIds);
                    }
                  }}
                  className="flex w-full cursor-pointer items-center justify-between text-left"
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-2xs">
                    <span
                      role="presentation"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Checkbox
                        id={`failed-payment-withholding-${balanceUniqueId}`}
                        checked={isSelected}
                        label={failedPayment.label ?? ''}
                        onChange={() => onToggleBalanceIds(balanceIds)}
                        labelTextClassName="truncate"
                      />
                    </span>
                    <Text
                      variant="p2"
                      fontWeight="semibold"
                      className="pl-[28px] text-red"
                    >
                      {FAILED_PAYMENTS_STRINGS['item.failed_prefix']}{' '}
                      {formatShortDate(failedPayment.failedOn)}
                    </Text>
                  </span>
                  <Money
                    value={-Math.abs(failedPayment.amount)}
                    format="DOLLARS_WITH_CENTS"
                    className="ml-sm shrink-0 text-body font-semibold"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(withholdingFullPayment === true ||
        (withholdingFullPayment === false &&
          withheldBalanceUniqueIds.length > 0)) && (
        <WithholdingWarning
          hasFutureSelectedPayment={hasFutureSelectedPayment}
          futureSelectedPayments={futureSelectedPayments}
        />
      )}
    </div>
  );
}

function WithholdingWarning({
  hasFutureSelectedPayment,
  futureSelectedPayments,
}: {
  hasFutureSelectedPayment: boolean;
  futureSelectedPayments: ConsumerFailedPaymentModel[];
}) {
  if (hasFutureSelectedPayment) {
    const selectedLabels = futureSelectedPayments
      .map((failedPayment) => failedPayment.label)
      .filter(Boolean)
      .join(' and ');

    return (
      <div className="flex flex-col gap-xs rounded border border-solid border-red bg-[#fff7f7] p-md text-navy">
        <Text fontWeight="semibold" className="text-h3-sm">
          {FAILED_PAYMENTS_STRINGS['withholding.advance_warning_title']}
        </Text>
        <Text variant="p2">
          <span className="font-semibold">{selectedLabels}</span>{' '}
          {FAILED_PAYMENTS_STRINGS['withholding.advance_warning_body']}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-xs rounded border border-solid border-red bg-[rgba(255,86,97,0.04)] p-lg text-navy">
      <Text fontWeight="semibold">
        {FAILED_PAYMENTS_STRINGS['withholding.warning_title']}
      </Text>
      <Text variant="body">
        {FAILED_PAYMENTS_STRINGS['withholding.warning_body']}
      </Text>
    </div>
  );
}
